import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { AuthService } from '../../auth/services/auth.service';
import { AdminPermisosService } from '../services/admin-permisos.service';
import { initFlowbite } from 'flowbite';
import { CommonModule } from '@angular/common';
import { RouterModule, Router, NavigationEnd } from '@angular/router';
import { SpinnerService } from '../spinner/spinner.service';
import { MatIconModule } from '@angular/material/icon';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-sidebar-admin',
  standalone: true,
  imports: [CommonModule, RouterModule, MatIconModule],
  templateUrl: './sidebar-admin.component.html',
  styleUrl: './sidebar-admin.component.scss'
})
export class SidebarAdminComponent implements OnInit, OnDestroy {
  authService = inject(AuthService);
  permisosService = inject(AdminPermisosService);
  spinnerService = inject(SpinnerService);
  router = inject(Router);

  menu: any[] = [];
  openMenuId: number | null = null;
  currentUrl: string = '';
  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    initFlowbite();

    // Detectar cambios de ruta
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      this.currentUrl = event.url;
      this.setActiveMenuFromUrl();
    });

    this.currentUrl = this.router.url;
    this.authService.user$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(response => {
      if (response) {
        this.getMenu(response.id);
      }
    });
  }

  getMenu(user_id: number) {
    this.spinnerService.startRequest();
    this.permisosService.getMenu(user_id).subscribe((response: any) => {
      if (response.success) {
        this.menu = response.data;
        setTimeout(() => {
          this.setActiveMenuFromUrl();
          this.spinnerService.endRequest();
        }, 300);
      } else {
        this.menu = [];
        this.spinnerService.endRequest();
      }
    });
  }

  toggleMenu(menuId: number) {
    if (this.openMenuId === menuId) {
      this.openMenuId = null;
    } else {
      this.openMenuId = menuId;
    }
  }

  /**
   * Se llama al hacer clic en un link de submenú, ANTES de que termine la navegación. Fija de una
   * vez cuál es el grupo "dueño" del clic — necesario porque dos grupos distintos pueden tener un
   * submenú que apunta a la MISMA url (ej. "Aliados"/"Confirmar Registro" existen tanto en
   * "Festival Oport" como en "Empleo Fest", porque ambos apuntan al mismo componente). Sin esto,
   * setActiveMenuFromUrl() no tiene forma de saber cuál de los dos se clickeó: solo ve la URL
   * resultante y siempre resuelve al primero que encuentra en el arreglo del menú.
   */
  onSubmenuClick(menuId: number): void {
    this.openMenuId = menuId;
  }

  isMenuOpen(menuId: number): boolean {
    return this.openMenuId === menuId;
  }

  isActiveRoute(url: string): boolean {
    return this.currentUrl === url;
  }

  /**
   * Un grupo se marca "activo" (resaltado) solo si es el que está actualmente abierto
   * (openMenuId), no si CUALQUIER grupo contiene una URL que matchee la ruta actual. Dos grupos
   * distintos del menú (ej. "Festival Oport" y "Empleo Fest") pueden compartir la misma página de
   * submenú (ej. "Confirmar Registro" → /evento-oportunidades, que ahora sirve ambos tipos de
   * evento) — comparar por URL en cada grupo por separado marcaba ambos como activos a la vez,
   * aunque solo uno estuviera realmente desplegado.
   */
  isGroupActive(item: any): boolean {
    if (item.group === 'false') {
      return this.isActiveRoute(item.url);
    } else if (item.submenu) {
      return this.openMenuId === item.menu_id;
    }
    return false;
  }

  setActiveMenuFromUrl() {
    // Si el grupo ya abierto (fijado por un clic explícito en onSubmenuClick, o por una
    // resolución anterior de este mismo método) sigue conteniendo la URL actual, se respeta —
    // así no se pisa el grupo que el usuario clickeó cuando dos grupos comparten la misma URL de
    // submenú (ver onSubmenuClick). Solo se recalcula "a ciegas" (primer match del arreglo) para
    // navegación directa: recarga de página, link externo, o adelante/atrás del navegador.
    const currentGroupStillMatches = this.menu.some(item =>
      item.menu_id === this.openMenuId &&
      item.group === 'true' &&
      item.submenu?.some((sub: any) => this.currentUrl === sub.url)
    );
    if (currentGroupStillMatches) return;

    // Encontrar el menú que contiene la ruta actual y abrirlo
    const activeMenu = this.menu.find(item => {
      if (item.group === 'true' && item.submenu) {
        return item.submenu.some((sub: any) => this.currentUrl === sub.url);
      }
      return false;
    });

    if (activeMenu) {
      this.openMenuId = activeMenu.menu_id;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}